import { useTranslation } from 'react-i18next';
import { BodyText, MaterialSymbol, withStyles, type AccountUserRole } from 'vopay';
import { Card, Skeleton, Title, UtilButton } from '#shared/components/UI';
import useUsers from '../useUsers';
import styles from './UserRolesTiles.module.scss';

const cn = withStyles(styles);

type UserRolesTilesProps = {
  roles: AccountUserRole[];
  roleCounts: Record<number, number>;
  fetching: boolean;
  /** Without it the tiles can't open a role, so they read as plain counts. */
  onRoleClick?: (role: AccountUserRole) => void;
  /** Without it there is no Create Roles button. */
  onCreateRoleClick?: () => void;
};

// The roles strip above the user table: a tile per role with its user count, or the empty state.
export default function UserRolesTiles({
  roles,
  roleCounts,
  fetching,
  onRoleClick,
  onCreateRoleClick,
}: UserRolesTilesProps) {
  const { userPermissions } = useUsers();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.UserRolesTiles' });

  if (!userPermissions.canViewRoles && !userPermissions.canManageRoles) return null;

  if (fetching) {
    return (
      <Card hoverEffect={false} className={cn('container')}>
        <Skeleton height="80px" />
      </Card>
    );
  }

  const canCreate = userPermissions.canManageRoles && !!onCreateRoleClick;

  const createButton = canCreate && (
    <UtilButton size="medium" iconLeft="add" onClick={onCreateRoleClick}>
      {t('createRole', { defaultValue: 'Create Roles' })}
    </UtilButton>
  );

  // A view-only user with no roles to look at gets nothing: the empty-state CTA doesn't apply to them.
  if (roles.length === 0 && !userPermissions.canManageRoles) return null;

  if (roles.length > 0) {
    return (
      <Card hoverEffect={false} className={cn('container')}>
        <div className={cn('header')}>
          <Title level="4" className={cn('headerTitle')}>
            {t('title', { defaultValue: 'Roles' })}
          </Title>

          {createButton}
        </div>

        <div className={cn('roleCards')}>
          {roles.map((role) => (
            <button key={role.RoleID} type="button" className={cn('roleCard')} onClick={() => onRoleClick?.(role)}>
              <span className={cn('roleCardName')}>{role.Name}</span>
              <span className={cn('roleCardCount')}>{roleCounts[role.RoleID] ?? 0}</span>
            </button>
          ))}
        </div>
      </Card>
    );
  }

  return (
    <Card hoverEffect={false} className={cn('container')}>
      <div className={cn('emptyState')}>
        <div className={cn('emptyInfo')}>
          <MaterialSymbol icon="groups" fillAmount={1} size="40px" className={cn('icon')} />

          <div className={cn('emptyText')}>
            <Title level="4" className={cn('headerTitle')}>
              {t('EmptyState.title', { defaultValue: 'Create Roles' })}
            </Title>

            <BodyText weight="md" className={cn('description')}>
              {t('EmptyState.description', {
                defaultValue:
                  'Roles let you group permissions and assign them to users in one step. Create your first role to get started.',
              })}
            </BodyText>
          </div>
        </div>

        {createButton}
      </div>
    </Card>
  );
}
